import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
  Autocomplete,
  Chip,
  useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { alertsService, Alert, CreateAlertPayload } from './services/AlertsService';
import { dashboardsService, Dashboard } from '../dashboards/services/DashboardsService';
import { logger } from 'utils/logger';
import ConfirmationDialog from 'components/ConfirmationDialog';
import ActiveAlertsTable from './components/ActiveAlertsTable';
import HistoricalAlertsTable from './components/HistoricalAlertsTable';

const log = logger.getLogger('AlertsPage');

const AlertsPage: React.FC = () => {
  // Form state
  const [message, setMessage] = useState('');
  const [siteChecked, setSiteChecked] = useState(false);
  const [hubChecked, setHubChecked] = useState(false);
  const [selectedDashboards, setSelectedDashboards] = useState<Dashboard[]>([]);

  // Data state
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(false);

  // UI state
  const [historicalExpanded, setHistoricalExpanded] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showEndAlertDialog, setShowEndAlertDialog] = useState(false);
  const [alertToEnd, setAlertToEnd] = useState<number | null>(null);

  // Split alerts into active and historical
  const activeAlerts = alerts.filter(alert => !alert.endedDate);
  const historicalAlerts = alerts.filter(alert => alert.endedDate);

  const theme = useTheme();

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      log.log('Starting to fetch alerts and dashboards...');
      
      // Fetch alerts
      const alertsResponse = await alertsService.getAlerts(true);
      log.log('Alerts response:', alertsResponse);
      setAlerts(alertsResponse.alerts);
      
      // Fetch dashboards without caching
      const dashboardsData = await dashboardsService.getDashboards(false);
      log.log('Dashboards response:', dashboardsData);
      
      if (Array.isArray(dashboardsData)) {
        setDashboards(dashboardsData);
      } else {
        setDashboards([]);
      }
    } catch (error) {
      log.error('Error fetching data:', error);
      setDashboards([]);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  // Form handlers
  const handleSiteChange = (checked: boolean) => {
    setSiteChecked(checked);
    if (checked) {
      setHubChecked(false);
      setSelectedDashboards([]);
    }
  };

  const handleHubChange = (checked: boolean) => {
    setHubChecked(checked);
    if (checked) {
      setSiteChecked(false);
    }
  };

  const handleDashboardsChange = (newValue: Dashboard[]) => {
    setSelectedDashboards(newValue);
    if (newValue.length > 0) {
      setSiteChecked(false);
    }
  };

  const handleReset = () => {
    setMessage('');
    setSiteChecked(false);
    setHubChecked(false);
    setSelectedDashboards([]);
  };

  const handleSubmitClick = () => {
    if (!message.trim()) {
      log.warn('Cannot submit empty message');
      return;
    }
    if (!siteChecked && !hubChecked && selectedDashboards.length === 0) {
      log.warn('Must select at least one location');
      return;
    }
    setShowSubmitDialog(true);
  };

  const handleSubmitConfirm = async () => {
    try {
      setLoading(true);
      
      const payload: CreateAlertPayload = {
        message: message.trim()
      };

      if (siteChecked) {
        payload.site = true;
      }
      if (hubChecked) {
        payload.hub = true;
      }
      if (selectedDashboards.length > 0) {
        payload.dashboards = selectedDashboards.map(d => d.id);
      }

      await alertsService.createAlert(payload);
      
      // Reset form and refresh data
      handleReset();
      await fetchData();
      
      setShowSubmitDialog(false);
    } catch (error) {
      log.error('Error creating alert:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEndAlertClick = (alertId: number) => {
    setAlertToEnd(alertId);
    setShowEndAlertDialog(true);
  };

  const handleEndAlertConfirm = async () => {
    if (alertToEnd === null) return;

    try {
      setLoading(true);
      await alertsService.endAlert(alertToEnd);
      await fetchData();
      setShowEndAlertDialog(false);
      setAlertToEnd(null);
    } catch (error) {
      log.error('Error ending alert:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      {/* Title */}
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: theme.palette.primary.main,
          mb: 3
        }}
      >
        Alerts
      </Typography>

      {/* Alert Form */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        {/* Message Text Box */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Alert Message"
            value={message}
            onChange={(e) => {
              if (e.target.value.length <= 1000) {
                setMessage(e.target.value);
              }
            }}
            helperText={`${message.length}/1000 characters`}
            variant="outlined"
          />
        </Box>

        {/* Options Row */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Site Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={siteChecked}
                onChange={(e) => handleSiteChange(e.target.checked)}
                disabled={hubChecked || selectedDashboards.length > 0}
              />
            }
            label="Site"
          />

          {/* Hub Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={hubChecked}
                onChange={(e) => handleHubChange(e.target.checked)}
                disabled={siteChecked}
              />
            }
            label="Hub"
          />

          {/* Dashboards Multiselect */}
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              multiple
              options={dashboards}
              groupBy={(option) => option.category?.name || 'Uncategorized'}
              getOptionLabel={(option) => option.name}
              value={selectedDashboards}
              onChange={(_, newValue) => handleDashboardsChange(newValue)}
              disabled={siteChecked}
              loading={loading}
              disableCloseOnSelect
              noOptionsText={loading ? "Loading dashboards..." : "No active dashboards"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dashboards"
                  placeholder="Select dashboards"
                  size="small"
                />
              )}
              renderValue={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />
          </Box>

          {/* Buttons */}
          <Button
            variant="outlined"
            color="primary"
            onClick={handleReset}
            sx={{ minWidth: 100 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitClick}
            disabled={!message.trim() || (!siteChecked && !hubChecked && selectedDashboards.length === 0)}
            sx={{ minWidth: 100 }}
          >
            Submit
          </Button>
        </Box>
      </Paper>

      {/* Active Alerts Table */}
      <Paper elevation={2} sx={{ mb: 4 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" component="h2">
            Active Alerts
          </Typography>
        </Box>
        <ActiveAlertsTable
          alerts={activeAlerts}
          dashboards={dashboards}
          onEndAlert={handleEndAlertClick}
          loading={loading}
        />
      </Paper>

      {/* Historical Alerts Table */}
      <Paper elevation={2}>
        <Box 
          sx={{ 
            p: 2, 
            borderBottom: 1, 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={() => setHistoricalExpanded(!historicalExpanded)}
        >
          <Typography variant="h6" component="h2">
            Historical Alerts
          </Typography>
          <IconButton size="small">
            {historicalExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={historicalExpanded}>
          <HistoricalAlertsTable
            alerts={historicalAlerts}
            dashboards={dashboards}
            loading={loading}
          />
        </Collapse>
      </Paper>

      {/* Submit Confirmation Dialog */}
      <ConfirmationDialog
        open={showSubmitDialog}
        title="Confirm Alert Creation"
        content={
          <Box>
            <Typography variant="body1" gutterBottom>
              <strong>Message:</strong> {message}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>Location:</strong>
            </Typography>
            <Box sx={{ pl: 2 }}>
              {siteChecked && (
                <Typography variant="body2">• Site</Typography>
              )}
              {hubChecked && (
                <Typography variant="body2">• Hub</Typography>
              )}
              {selectedDashboards.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                    Dashboards:
                  </Typography>
                  {selectedDashboards.map((dashboard) => (
                    <Typography key={dashboard.id} variant="body2" sx={{ pl: 2 }}>
                      • {dashboard.name}
                    </Typography>
                  ))}
                </>
              )}
            </Box>
          </Box>
        }
        onConfirm={handleSubmitConfirm}
        onCancel={() => setShowSubmitDialog(false)}
        confirmButtonText="Create Alert"
        cancelButtonText="Cancel"
        confirmButtonColor="primary"
      />

      {/* End Alert Confirmation Dialog */}
      <ConfirmationDialog
        open={showEndAlertDialog}
        title="Confirm End Alert"
        content="Are you sure you want to end this alert? This action cannot be undone."
        onConfirm={handleEndAlertConfirm}
        onCancel={() => {
          setShowEndAlertDialog(false);
          setAlertToEnd(null);
        }}
        confirmButtonText="End Alert"
        cancelButtonText="Cancel"
        confirmButtonColor="primary"
      />
    </Container>
  );
};

export default AlertsPage;
